import React, { useEffect, useMemo, useState } from "react";
import { recommendJobsFromCv } from "../../services/api";
import {
  enrichJobsWithLicensedSponsorStatus,
} from "../../services/licensedSponsorService";
import AiJobBoardSupportPopup from "./AiJobBoardSupportPopup";
import { defaultFilters, filterJobs, filterOptions } from "./jobFilters";
import {
  addStoredJob,
  getCvPreview,
  getStoredJobIds,
  getStoredRecommendations,
  JobCard,
  setCvPreview,
  setStoredRecommendations,
  SummaryCard,
  toggleStoredJob,
} from "./jobUi";

const progressSteps = [
  "Analysing your CV",
  "Extracting your key skills",
  "Searching live UK jobs",
  "Ranking roles by match score",
  "Preparing your recommendations",
];

function FilterSelect({ label, value, options, onChange }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">
        {label}
      </span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
}

function toPortalJob(job) {
  return {
    ...job,
    source: job.source || job.source_label || (job.provider === "reed" ? "Jobs by Reed" : "Jobs by Adzuna"),
    summary: job.match_summary || job.description,
    salaryMin: job.salaryMin ?? job.salary_min,
    salaryMax: job.salaryMax ?? job.salary_max,
    matchScore: job.matchScore ?? job.match_score ?? 0,
    skillsMatched: job.skillsMatched || job.matched_skills || [],
    missingKeywords: job.missingKeywords || job.missing_keywords || [],
    role: job.role || "All roles",
  };
}

function UploadPanel({
  selectedFile,
  setSelectedFile,
  uploadError,
  setUploadError,
  onFindJobs,
  isLoading,
  targetRole,
  setTargetRole,
  location,
  setLocation,
}) {
  return (
    <section className="rounded-3xl border border-blue-100 bg-white p-5 shadow-sm shadow-blue-100/60 md:p-6">
      <div className="grid gap-6 lg:grid-cols-[1fr_380px] lg:items-center">
        <div>
          <p className="text-sm font-bold uppercase tracking-wide text-indigo-600">
            AI CV-based job matching
          </p>
          <h2 className="mt-2 text-2xl font-black text-slate-950">Upload CV</h2>
          <p className="mt-2 text-slate-600">
            Upload a PDF or DOCX CV and JobZam will analyse your skills, search
            live UK roles, and rank the best matches for you.
          </p>
          <p className="mt-3 rounded-2xl bg-blue-50 p-3 text-sm font-semibold text-blue-900">
            Always verify job details on the official employer or job board
            website before applying.
          </p>
        </div>

        <div className="rounded-2xl border border-dashed border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50 p-4">
          <input
            type="file"
            accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            disabled={isLoading}
            onChange={(event) => {
              setUploadError("");
              setSelectedFile(event.target.files?.[0] || null);
            }}
            className="block w-full text-sm text-slate-700 file:mr-4 file:rounded-xl file:border-0 file:bg-gradient-to-r file:from-indigo-600 file:to-blue-600 file:px-4 file:py-2.5 file:text-sm file:font-bold file:text-white hover:file:from-indigo-700 hover:file:to-blue-700 disabled:opacity-60"
          />

          <label className="mt-4 block">
            <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">
              Target role
            </span>
            <input
              value={targetRole}
              disabled={isLoading}
              onChange={(event) => setTargetRole(event.target.value)}
              placeholder="IT, Civil Engineer, Healthcare, Chef, Marketing Executive"
              className="w-full rounded-xl border border-white bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
            />
            {!targetRole.trim() ? (
              <span className="mt-2 block text-xs font-semibold text-amber-700">
                Add a target role for better recommendations.
              </span>
            ) : null}
          </label>

          <label className="mt-4 block">
            <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">
              Location
            </span>
            <input
              value={location}
              disabled={isLoading}
              onChange={(event) => setLocation(event.target.value)}
              placeholder="London"
              className="w-full rounded-xl border border-white bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
            />
          </label>

          {selectedFile ? (
            <div className="mt-4 rounded-xl bg-white p-3 text-sm text-slate-700">
              <span className="font-bold">Selected:</span> {selectedFile.name}
            </div>
          ) : null}

          {uploadError ? (
            <p className="mt-3 text-sm font-semibold text-rose-600">{uploadError}</p>
          ) : null}

          <button
            type="button"
            onClick={onFindJobs}
            disabled={isLoading}
            className="mt-4 w-full rounded-xl bg-gradient-to-r from-indigo-600 to-blue-600 px-4 py-3 text-sm font-black text-white shadow-sm shadow-indigo-200 transition hover:from-indigo-700 hover:to-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isLoading ? "Analysing CV..." : "Find Recommended Jobs"}
          </button>
        </div>
      </div>
    </section>
  );
}

function AiProgressCard({ activeStep }) {
  return (
    <section className="rounded-3xl border border-indigo-100 bg-gradient-to-br from-white via-blue-50 to-indigo-50 p-6 shadow-lg shadow-blue-100/70">
      <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-sm font-bold uppercase tracking-wide text-indigo-600">
            AI analysis in progress
          </p>
          <h2 className="mt-2 text-2xl font-black text-slate-950">
            Building your ranked job recommendations
          </h2>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            JobZam is reading your CV, finding relevant live UK jobs, and scoring
            each role against your skills.
          </p>
        </div>
        <div className="h-16 w-16 animate-spin rounded-full border-4 border-indigo-100 border-t-indigo-600" />
      </div>

      <div className="mt-6 grid gap-3 md:grid-cols-5">
        {progressSteps.map((step, index) => {
          const isDone = index < activeStep;
          const isActive = index === activeStep;

          return (
            <div
              key={step}
              className={
                "rounded-2xl border p-3 text-sm font-bold transition " +
                (isDone || isActive
                  ? "border-indigo-200 bg-white text-indigo-700 shadow-sm"
                  : "border-slate-200 bg-white/60 text-slate-500")
              }
            >
              <span className="mb-2 block text-xs text-slate-400">0{index + 1}</span>
              {step}
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default function AllJobsPage() {
  const [filters, setFilters] = useState(defaultFilters);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadError, setUploadError] = useState("");
  const [targetRole, setTargetRole] = useState("");
  const [location, setLocation] = useState("London");
  const [cvPreview, setLocalCvPreview] = useState(() => getCvPreview());
  const [recommendationPayload, setRecommendationPayload] = useState(() => getStoredRecommendations());
  const [isLoading, setIsLoading] = useState(false);
  const [isPreparingResults, setIsPreparingResults] = useState(false);
  const [hasSearched, setHasSearched] = useState(() => !!getStoredRecommendations());
  const [activeStep, setActiveStep] = useState(0);
  const [savedIds, setSavedIds] = useState(() => getStoredJobIds("saved"));
  const [appliedIds, setAppliedIds] = useState(() => getStoredJobIds("applied"));
  const [enrichedJobs, setEnrichedJobs] = useState([]);
  const [supportPopupTriggerKey, setSupportPopupTriggerKey] = useState(null);

  useEffect(() => {
    if (!isLoading) return undefined;

    const timer = window.setInterval(() => {
      setActiveStep((current) => Math.min(current + 1, progressSteps.length - 1));
    }, 1200);

    return () => window.clearInterval(timer);
  }, [isLoading]);

  useEffect(() => {
    let cancelled = false;

    async function enrich() {
      const portalJobs = (recommendationPayload?.jobs || []).map(toPortalJob);
      if (!portalJobs.length) {
        setEnrichedJobs([]);
        setIsPreparingResults(false);
        return;
      }

      setIsPreparingResults(true);
      try {
        const enriched = await enrichJobsWithLicensedSponsorStatus(portalJobs);
        if (!cancelled) setEnrichedJobs(enriched);
      } catch {
        if (!cancelled) setEnrichedJobs(portalJobs);
      } finally {
        if (!cancelled) setIsPreparingResults(false);
      }
    }

    enrich();
    return () => {
      cancelled = true;
    };
  }, [recommendationPayload]);

  const jobs = enrichedJobs;
  const visibleJobs = useMemo(() => filterJobs(jobs, filters), [filters, jobs]);
  const providerLabel = recommendationPayload?.provider_used === "reed" ? "Jobs by Reed" : "Jobs by Adzuna";
  const fallbackNote = recommendationPayload?.fallback_used ? "Adzuna fallback used." : "Reed primary search.";
  const topMatch = jobs[0];
  const averageMatch = jobs.length
    ? Math.round(jobs.reduce((total, job) => total + (job.matchScore || 0), 0) / jobs.length)
    : 0;

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const handleFindJobs = async () => {
    if (!selectedFile) {
      setUploadError("Choose a PDF or DOCX CV first.");
      return;
    }

    setHasSearched(true);
    setUploadError("");
    setIsLoading(true);
    setIsPreparingResults(false);
    setActiveStep(0);
    setRecommendationPayload(null);
    setLocalCvPreview(setCvPreview(selectedFile));

    try {
      const data = await recommendJobsFromCv({
        file: selectedFile,
        targetRole,
        location: location || "London",
      });
      setIsPreparingResults(!!data?.jobs?.length);
      setRecommendationPayload(setStoredRecommendations(data));
      setSupportPopupTriggerKey(data?.jobs?.length ? Date.now() : null);
    } catch (error) {
      const fallback = {
        search_keyword: "Live recommendations unavailable",
        location: location || "London",
        provider_used: null,
        fallback_used: false,
        count: 0,
        extracted_skills: [],
        jobs: [],
      };
      setIsPreparingResults(false);
      setRecommendationPayload(setStoredRecommendations(fallback));
      setSupportPopupTriggerKey(null);
      setUploadError(error.message || "Could not load live jobs.");
    } finally {
      setIsLoading(false);
      setActiveStep(progressSteps.length - 1);
    }
  };

  const handleSave = (jobId) => {
    setSavedIds(toggleStoredJob("saved", jobId));
  };

  const handleApply = (job) => {
    if (job.redirect_url) {
      window.open(job.redirect_url, "_blank", "noopener,noreferrer");
    }
    setAppliedIds(addStoredJob("applied", job.id));
  };

  const handleTailor = () => {
    window.alert("Tailor CV from Jobs Portal is coming soon.");
  };

  return (
    <div className="space-y-6">
      <AiJobBoardSupportPopup
        shouldPrompt={!isLoading && !isPreparingResults && !uploadError && jobs.length > 0}
        triggerKey={supportPopupTriggerKey}
      />

      <section className="relative overflow-hidden rounded-3xl border border-blue-100 bg-gradient-to-br from-white via-blue-50 to-indigo-50 shadow-xl shadow-blue-100/70">
        <div className="grid gap-8 p-6 md:grid-cols-[1.4fr_0.9fr] md:p-8">
          <div className="relative">
            <div className="inline-flex rounded-full border border-indigo-100 bg-white px-3 py-1 text-sm font-semibold text-indigo-700 shadow-sm">
              Premium feature
            </div>
            <h1 className="mt-5 max-w-3xl text-4xl font-black tracking-tight text-slate-950 sm:text-5xl">
              AI-ranked jobs from your uploaded CV.
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-7 text-slate-600">
              Upload your CV to search live UK jobs and see recommendations
              ranked by keyword and skill match.
            </p>
          </div>

          <div className="relative rounded-3xl border border-white bg-white/85 p-5 shadow-lg shadow-blue-100/70 backdrop-blur">
            <p className="text-sm font-semibold text-slate-500">Current status</p>
            <p className="mt-3 text-2xl font-black text-slate-950">
              {jobs.length ? "Recommendations ready" : isLoading ? "AI is analysing" : "Upload CV to start"}
            </p>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              {jobs.length && cvPreview
                ? `AI-ranked recommendations based on ${cvPreview.name}.`
                : "Recommendations will appear only after you upload a CV here."}
            </p>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-sky-100 bg-sky-50 px-4 py-3 text-sm font-bold text-sky-800 shadow-sm shadow-sky-100/60">
        UK-first live job results powered by Reed, with Adzuna fallback.
      </section>

      <UploadPanel
        selectedFile={selectedFile}
        setSelectedFile={setSelectedFile}
        uploadError={uploadError}
        setUploadError={setUploadError}
        onFindJobs={handleFindJobs}
        isLoading={isLoading}
        targetRole={targetRole}
        setTargetRole={setTargetRole}
        location={location}
        setLocation={setLocation}
      />

      {(isLoading || isPreparingResults) ? <AiProgressCard activeStep={activeStep} /> : null}

      {!isLoading && !isPreparingResults && hasSearched && !uploadError && jobs.length === 0 ? (
        <section className="rounded-3xl border border-dashed border-slate-300 bg-white p-8 text-center">
          <h3 className="text-lg font-bold text-slate-950">
            No jobs found for this role/location.
          </h3>
          <p className="mt-2 text-sm text-slate-600">
            Try a broader role or nearby city.
          </p>
        </section>
      ) : null}

      {!jobs.length || isLoading || isPreparingResults ? null : (
        <>
          <section className="rounded-3xl border border-indigo-100 bg-indigo-50 p-4 text-sm font-semibold text-indigo-900">
            AI-ranked recommendations based on your uploaded CV. {providerLabel}. {fallbackNote}
            Always verify job details on the official employer or job board website before applying.
          </section>

          <section className="rounded-2xl border border-blue-100 bg-white px-4 py-3 text-sm font-bold text-blue-900 shadow-sm">
            Showing roles for {recommendationPayload.selected_search_role || recommendationPayload.search_keyword} in{" "}
            {recommendationPayload.location || location || "United Kingdom"}, ranked by your CV match.
          </section>

          <section className="grid gap-4 md:grid-cols-3">
            <SummaryCard
              label="Live recommendations"
              value={jobs.length}
              detail={`Search focus: ${recommendationPayload.selected_search_role || recommendationPayload.search_keyword}`}
            />
            <SummaryCard
              label="Average match score"
              value={`${averageMatch}%`}
              detail={topMatch?.title || "Ranked by CV keyword overlap"}
            />
            <SummaryCard
              label="High match roles"
              value={jobs.filter((job) => job.matchScore >= 85).length}
              detail="AI-ranked from live search"
            />
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm md:p-5">
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                <label className="block">
                  <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">
                    Search recommendations
                  </span>
                  <input
                    value={filters.query}
                    onChange={(event) => updateFilter("query", event.target.value)}
                    placeholder="Search by title, skill, company, or keyword"
                    className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
                  />
                </label>

                <FilterSelect
                  label="Location"
                  value={filters.location}
                  options={filterOptions.location}
                  onChange={(value) => updateFilter("location", value)}
                />
                <FilterSelect
                  label="Salary"
                  value={filters.salary}
                  options={filterOptions.salary}
                  onChange={(value) => updateFilter("salary", value)}
                />
                <FilterSelect
                  label="Sponsorship"
                  value={filters.licensedSponsor}
                  options={filterOptions.licensedSponsor}
                  onChange={(value) => updateFilter("licensedSponsor", value)}
                />
              </div>
              <button
                type="button"
                onClick={() => setFilters(defaultFilters)}
                className="rounded-xl bg-slate-100 px-4 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-200"
              >
                Reset filters
              </button>
            </div>
          </section>

          <section className="space-y-4">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="text-2xl font-black text-slate-950">
                  AI-ranked recommendations based on your uploaded CV
                </h2>
                <p className="text-sm text-slate-600">
                  Showing {visibleJobs.length} role{visibleJobs.length === 1 ? "" : "s"}. {providerLabel}.
                </p>
              </div>
            </div>

            {visibleJobs.length ? (
              visibleJobs.map((job) => (
                <JobCard
                  key={job.id}
                  job={job}
                  saved={savedIds.includes(job.id)}
                  applied={appliedIds.includes(job.id)}
                  onSave={handleSave}
                  onApply={handleApply}
                  onTailor={handleTailor}
                  applyLabel="Apply on official site"
                  tailorLabel="Tailor CV coming soon"
                />
              ))
            ) : (
              <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-8 text-center">
                <h3 className="text-lg font-bold text-slate-950">
                  No recommendations found
                </h3>
                <p className="mt-2 text-sm text-slate-600">
                  Try clearing a filter or searching for a broader skill.
                </p>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
